import React from 'react';

const ForTeachersPage: React.FC = () => {
  console.log('ForTeachersPage rendered - dedicated page for teacher information and commission structure');

  const commissionTiers = [
    { tier: 'Tier 1', students: '1–3', rate: 10, stripeFee: 3, teacherShare: (100 - (10 + 3)), framing: '"Starter Tier" — You\'re just getting going. We\'re here to support.' },
    { tier: 'Tier 2', students: '4–7', rate: 9, stripeFee: 3, teacherShare: (100 - (9 + 3)), framing: '"Momentum Tier" — You\'re building your studio. Let\'s go.' },
    { tier: 'Tier 3', students: '8–11', rate: 8, stripeFee: 3, teacherShare: (100 - (8 + 3)), framing: '"Established Tier" — More students, more earnings, less commission.' },
    { tier: 'Tier 4', students: '12–15', rate: 6.5, stripeFee: 3, teacherShare: (100 - (6.5 + 3)), framing: '"Pro Tier" — You\'re thriving. We take less because you\'re bringing more.' },
    { tier: 'Tier 5', students: '16+', rate: 5, stripeFee: 3, teacherShare: (100 - (5 + 3)), framing: '"Partner Tier" — You\'re a top performer. Keep more of what you earn.' },
  ];

  return (
    <div className="bg-white text-gray-800">
      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-6">
            <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-6">
            For Teachers
          </h1>
          <p className="mt-4 max-w-3xl mx-auto text-xl sm:text-2xl text-gray-700 leading-relaxed">
            Focus on teaching, we handle the rest
          </p>
          <p className="mt-4 max-w-3xl mx-auto text-lg text-gray-600">
            No sign-up costs or hidden fees
          </p>
        </div>
      </section>

      {/* Commission Structure Section */}
      <section className="py-20 sm:py-28 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-6xl font-bold text-yellow-600 mb-4">FREE to Join</div>
            <div className="text-lg text-gray-500 mb-8">No sign-up costs or hidden fees</div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 border border-yellow-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Commission Structure</h2>
              <p className="text-lg text-gray-600 text-center mb-8">
                We take a small percentage to cover server costs and Stripe fees, 
                delivering you an easy-to-use calendar and booking system. Our 
                commission decreases as your studio grows.
              </p>
              
              <div className="space-y-4">
                {commissionTiers.map((tier, index) => {
                  // Calculate projected monthly earnings (assuming $60 lesson, 4 lessons per student per month)
                  const lessonPrice = 60;
                  const lessonsPerStudentPerMonth = 4;
                  const grossPerStudent = lessonPrice * lessonsPerStudentPerMonth; // $240 per student
                  
                  // Calculate range based on min and max students in tier
                  const [minStudents, maxStudents] = tier.students === '1–3' ? [1, 3] : 
                                                    tier.students === '4–7' ? [4, 7] : 
                                                    tier.students === '8–11' ? [8, 11] : 
                                                    tier.students === '12–15' ? [12, 15] : [16, null];
                  
                  const minMonthly = Math.round(minStudents * grossPerStudent * (tier.teacherShare / 100));
                  const maxMonthly = maxStudents ? Math.round(maxStudents * grossPerStudent * (tier.teacherShare / 100)) : null;
                  
                  return (
                    <div key={tier.tier} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                        <div className="flex items-center space-x-3">
                          <div className={`w-4 h-4 rounded-full ${
                            index === 0 ? 'bg-red-400' :
                            index === 1 ? 'bg-orange-400' :
                            index === 2 ? 'bg-yellow-400' :
                            index === 3 ? 'bg-green-400' : 'bg-emerald-500'
                          }`}></div>
                          <div>
                            <div className="text-lg font-semibold text-gray-900">{tier.students} students</div>
                            <div className="text-sm text-gray-500">{tier.tier}</div>
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{tier.teacherShare}%</div>
                          <div className="text-sm text-gray-500">you keep</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-blue-600">
                            {maxStudents ? `$${minMonthly}-${maxMonthly}` : `$${minMonthly}+`}
                          </div>
                          <div className="text-sm text-gray-500">monthly*</div>
                        </div>
                      </div>
                      <div className="mt-3 text-sm text-gray-600 italic">
                        {tier.framing}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-sm text-gray-500 mt-6 text-center">
                *Projected earnings based on $60 lesson price, 4 lessons per student monthly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Teacher Benefits Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Why Teachers Love LessonLynx
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              You became a music teacher to share your craft — not chase payments or juggle tech tools. 
              LessonLynx handles the overhead so you can focus on the music.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl shadow-lg border border-blue-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Free to Join</h3>
              <p className="text-gray-700">No listing fees, no subscriptions — just open the door to new students.</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-xl shadow-lg border border-green-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Guaranteed Payouts</h3>
              <p className="text-gray-700">We manage billing and cancellations so you're paid promptly and fairly.</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl shadow-lg border border-purple-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Centralized Booking & Messaging</h3>
              <p className="text-gray-700">Keep it all in one clean, easy-to-use dashboard.</p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-8 rounded-xl shadow-lg border border-yellow-200">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Free Qualified Leads</h3>
              <p className="text-gray-700">Get connected with students based on compatibility and preferences.</p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-xl shadow-lg border border-teal-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Scales With You</h3>
              <p className="text-gray-700">As your student count grows, our commission goes down — because success should be rewarded.</p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-8 rounded-xl shadow-lg border border-rose-200">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">The Best Service, Best Rate</h3>
              <p className="text-gray-700">Simple verification system and referral program with industry-leading rates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
            Ready to Build Your Studio?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of teachers who are growing their music studios with LessonLynx. 
            Start earning more while focusing on what you love most — teaching music.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/about-us" 
              className="inline-flex items-center justify-center px-8 py-4 border border-white text-lg font-medium rounded-lg text-white hover:bg-white hover:text-blue-700 transition-colors duration-200"
            >
              Learn More About Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForTeachersPage; 